/**
 * GoalCardGrid — the grid view of the Goals page (Epic 18 grid arm).
 *
 * Mirrors ClientCardGrid.tsx's container exactly
 * (grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4) and maps each goal to
 * a GoalCard. Each card is wrapped in a motion.div with a staggered entrance
 * matching GoalsScorecards' cadence (the goals surface already staggers its
 * scorecards), so the grid reads as part of the same motion family.
 *
 * The grid is the `view === "grid"` arm of the Goals page's grid/table switch;
 * the page wires onEdit / onTogglePause / onDelete the same way it wires
 * GoalsTable. Status/badge logic lives entirely in GoalCard (which reuses
 * lib/goalPageDisplay) — this component only lays cards out.
 */

import { motion } from "motion/react";
import { GoalCard } from "./GoalCard";
import type { GoalListItem } from "../../lib/goalPageDisplay";

export interface GoalCardGridProps {
  goals: GoalListItem[];
  canManage: boolean;
  onGoalClick?: (goal: GoalListItem) => void;
  onEdit?: (goal: GoalListItem) => void;
  onTogglePause?: (goal: GoalListItem) => void;
  onDelete?: (goal: GoalListItem) => void;
}

export function GoalCardGrid({
  goals,
  canManage,
  onGoalClick,
  onEdit,
  onTogglePause,
  onDelete,
}: GoalCardGridProps) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="goal-card-grid"
    >
      {goals.map((goal, index) => (
        <motion.div
          key={goal.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 * index, duration: 0.2, ease: "easeOut" }}
        >
          <GoalCard
            goal={goal}
            canManage={canManage}
            onGoalClick={onGoalClick}
            onEdit={onEdit}
            onTogglePause={onTogglePause}
            onDelete={onDelete}
          />
        </motion.div>
      ))}
    </div>
  );
}
